import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CLOUDFLARE_CONFIG } from '../config/api';
import apiService from '../services/apiService';

const PortfolioDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [portfolioData, setPortfolioData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [imageUploads, setImageUploads] = useState({
    heroImage: { file: null, status: '', uploading: false },
    image1: { file: null, status: '', uploading: false },
    image2: { file: null, status: '', uploading: false },
    image3: { file: null, status: '', uploading: false }
  });

  useEffect(() => {
    if (id) {
      fetchPortfolioDetails();
    }
  }, [id]);

  const fetchPortfolioDetails = async () => {
    try {
      setLoading(true);
      
      const data = await apiService.get(`/portfolios/${id}`);
      setPortfolioData(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching portfolio details:', err);
      setError('Failed to load portfolio details');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setPortfolioData({
      ...portfolioData,
      [field]: value
    });
  };

  const handleFileChange = (imageKey, file) => {
    setImageUploads({
      ...imageUploads,
      [imageKey]: { ...imageUploads[imageKey], file: file, status: '' }
    });
  };

  const uploadToCloudflare = async (imageKey, file) => {
    try {
      // Update status
      setImageUploads(prev => ({
        ...prev,
        [imageKey]: { ...prev[imageKey], status: 'Uploading to Cloudflare...', uploading: true }
      }));

      // Rename file with CBREX prefix
      const renamedFile = new File([file], `CBREX_${id}_${file.name}`, {
        type: file.type,
      });

      // Upload to Cloudflare - try direct first, fall back to proxies if CORS blocks
      const cloudflareFormData = new FormData();
      cloudflareFormData.append("file", renamedFile);

      let cloudflareResponse = null;

      // Try direct upload first (no proxy)
      try {
        const directResponse = await fetch(CLOUDFLARE_CONFIG.IMAGES_ENDPOINT, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${CLOUDFLARE_CONFIG.API_TOKEN}`,
          },
          body: cloudflareFormData,
        });

        if (directResponse.ok) {
          cloudflareResponse = await directResponse.json();
        }
      } catch (error) {
        console.log('Direct upload failed (likely CORS), trying proxies...', error);
      }

      // Only use proxies if direct upload failed
      if (!cloudflareResponse) {
        const corsProxies = [
          "https://cors-anywhere.herokuapp.com/",
          "https://api.allorigins.win/raw?url=",
          "https://corsproxy.io/?",
        ];

        for (const proxy of corsProxies) {
          try {
            const response = await fetch(
              proxy + CLOUDFLARE_CONFIG.IMAGES_ENDPOINT,
              {
                method: "POST",
                headers: {
                  Authorization: `Bearer ${CLOUDFLARE_CONFIG.API_TOKEN}`,
                },
                body: cloudflareFormData,
              }
            );

            if (response.ok) {
              cloudflareResponse = await response.json();
              break;
            }
          } catch (error) {
            console.log(`Proxy ${proxy} failed:`, error);
            continue;
          }
        }
      }

      if (!cloudflareResponse) {
        throw new Error("All CORS proxies failed");
      }

      if (cloudflareResponse.success && cloudflareResponse.result.variants) {
        const imageLink = cloudflareResponse.result.variants[0];
        
        // Update portfolio data with new image link
        const linkField = imageKey === 'heroImage' ? 'heroImageLink' : 
                         imageKey === 'image1' ? 'image1Link' :
                         imageKey === 'image2' ? 'image2Link' : 'image3Link';
        
        // Update local state first
        const updatedPortfolioData = {
          ...portfolioData,
          [linkField]: imageLink
        };
        
        setPortfolioData(updatedPortfolioData);

        setImageUploads(prev => ({
          ...prev,
          [imageKey]: { 
            file: null, 
            status: 'Saving to database...', 
            uploading: true 
          }
        }));

        // Automatically save to database after successful Cloudflare upload
        try {
          const requestBody = {
            userId: 3,
            title: updatedPortfolioData.title || "",
            description: updatedPortfolioData.description || "",
            heroImageLink: updatedPortfolioData.heroImageLink || "",
            image1Link: updatedPortfolioData.image1Link || "",
            image2Link: updatedPortfolioData.image2Link || "",
            image3Link: updatedPortfolioData.image3Link || ""
          };

          await apiService.put(`/portfolios?id=${id}`, requestBody);

          setImageUploads(prev => ({
            ...prev,
            [imageKey]: { 
              file: null, 
              status: 'Upload and save successful!', 
              uploading: false 
            }
          }));

        } catch (saveError) {
          console.error('Error saving portfolio after image upload:', saveError);
          setImageUploads(prev => ({
            ...prev,
            [imageKey]: { 
              file: null, 
              status: 'Upload successful, but save failed!', 
              uploading: false 
            }
          }));
          throw saveError;
        }

        return imageLink;
      } else {
        throw new Error("Cloudflare upload failed");
      }
    } catch (error) {
      console.error(`Error uploading ${imageKey}:`, error);
      setImageUploads(prev => ({
        ...prev,
        [imageKey]: { ...prev[imageKey], status: 'Upload failed!', uploading: false }
      }));
      throw error;
    }
  };

  const handleImageUpload = async (imageKey) => {
    const file = imageUploads[imageKey].file;
    if (!file) return;

    try {
      await uploadToCloudflare(imageKey, file);
    } catch (error) {
      console.error(`Failed to upload ${imageKey}:`, error);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      const requestBody = {
        userId: 3,
        title: portfolioData.title || "",
        description: portfolioData.description || "",
        heroImageLink: portfolioData.heroImageLink || "",
        image1Link: portfolioData.image1Link || "",
        image2Link: portfolioData.image2Link || "",
        image3Link: portfolioData.image3Link || ""
      };

      await apiService.put(`/portfolios?id=${id}`, requestBody);
      
      alert('Portfolio updated successfully!');
      
    } catch (err) {
      console.error('Error updating portfolio:', err);
      alert('Failed to update portfolio. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="manage-properties">
        <div className="page-header">
          <h1>Portfolio Details</h1>
        </div>
        <div className="loading-state">
          <p>Loading portfolio details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="manage-properties">
        <div className="page-header">
          <h1>Portfolio Details</h1>
        </div>
        <div className="error-state">
          <p>{error}</p>
          <button onClick={fetchPortfolioDetails} className="retry-button">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!portfolioData) {
    return (
      <div className="manage-properties">
        <div className="page-header">
          <h1>Portfolio Details</h1>
        </div>
        <div className="error-state">
          <p>Portfolio not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="manage-properties">
      <div className="page-header">
        <h1>Portfolio Details</h1>
      </div>

      <div className="content-container">
        {/* Top Section: Form fields on left, Hero image on right */}
        <div className="portfolio-top-section" style={{ display: 'flex', gap: '20px', marginBottom: '30px' }}>
          <div className="portfolio-form-section" style={{ flex: '1' }}>
            <div className="form-section">
              <div className="form-row">
                <label htmlFor="title">Title:</label>
                <input
                  type="text"
                  id="title"
                  className="form-input"
                  value={portfolioData.title || ''}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                />
              </div>
              
              <div className="form-row">
                <label htmlFor="description">Description:</label>
                <textarea
                  id="description"
                  className="form-textarea"
                  value={portfolioData.description || ''}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  rows="6"
                />
              </div>

              {/* Update Button */}
              <div className="form-actions" style={{ marginTop: '20px' }}>
                <button 
                  type="button" 
                  className="btn-primary"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? 'Updating...' : 'Update Portfolio'}
                </button>
              </div>
            </div>
          </div>

          {/* Hero Image Display */}
          <div className="portfolio-hero-section" style={{ flex: '1' }}>
            <div className="form-section">
              <h3>Hero Image</h3>
              {portfolioData.heroImageLink ? (
                <img 
                  src={portfolioData.heroImageLink} 
                  alt="Hero"
                  style={{ 
                    width: '100%', 
                    maxWidth: '400px', 
                    height: 'auto', 
                    border: '1px solid var(--cbre-border)',
                    borderRadius: '4px',
                    marginBottom: '15px'
                  }}
                />
              ) : (
                <div style={{ 
                  width: '100%', 
                  height: '200px', 
                  backgroundColor: 'var(--cbre-bg-secondary)',
                  border: '1px solid var(--cbre-border)',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--cbre-text-secondary)',
                  marginBottom: '15px'
                }}>
                  No hero image
                </div>
              )}

              {/* Hero Image Upload */}
              <div className="form-row">
                <label htmlFor="heroImageUpload">Update Hero Image:</label>
                <div className="upload-controls">
                  <input
                    type="file"
                    id="heroImageUpload"
                    accept="image/*"
                    onChange={(e) => handleFileChange('heroImage', e.target.files[0])}
                    className="form-input"
                    style={{ marginBottom: '5px' }}
                  />
                  <button
                    type="button"
                    onClick={() => handleImageUpload('heroImage')}
                    disabled={!imageUploads.heroImage.file || imageUploads.heroImage.uploading}
                    className="btn-primary"
                  >
                    {imageUploads.heroImage.uploading ? 'Uploading...' : 'Upload'}
                  </button>
                  {imageUploads.heroImage.status && (
                    <span className="upload-status" style={{ marginLeft: '10px', fontSize: '14px' }}>
                      {imageUploads.heroImage.status}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Section: Three images in a row */}
        <div className="portfolio-images-section">
          <div className="form-section">
            <h3>Gallery Images</h3>
            <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
              {/* Image 1 */}
              <div style={{ flex: '1', minWidth: '250px' }}>
                <h4>Image 1</h4>
                {portfolioData.image1Link ? (
                  <img 
                    src={portfolioData.image1Link} 
                    alt="Image 1"
                    style={{ 
                      width: '100%', 
                      height: '200px', 
                      objectFit: 'cover',
                      border: '1px solid var(--cbre-border)',
                      borderRadius: '4px'
                    }}
                  />
                ) : (
                  <div style={{ 
                    width: '100%', 
                    height: '200px', 
                    backgroundColor: 'var(--cbre-bg-secondary)',
                    border: '1px solid var(--cbre-border)',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--cbre-text-secondary)'
                  }}>
                    No image
                  </div>
                )}
                <div className="upload-controls" style={{ marginTop: '10px' }}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileChange('image1', e.target.files[0])}
                    className="form-input"
                    style={{ marginBottom: '5px' }}
                  />
                  <button
                    type="button"
                    onClick={() => handleImageUpload('image1')}
                    disabled={!imageUploads.image1.file || imageUploads.image1.uploading}
                    className="btn-primary"
                  >
                    {imageUploads.image1.uploading ? 'Uploading...' : 'Upload'}
                  </button>
                  {imageUploads.image1.status && (
                    <div className="upload-status" style={{ fontSize: '12px', marginTop: '5px' }}>
                      {imageUploads.image1.status}
                    </div>
                  )}
                </div>
              </div>

              {/* Image 2 */}
              <div style={{ flex: '1', minWidth: '250px' }}>
                <h4>Image 2</h4>
                {portfolioData.image2Link ? (
                  <img 
                    src={portfolioData.image2Link} 
                    alt="Image 2"
                    style={{ 
                      width: '100%', 
                      height: '200px', 
                      objectFit: 'cover',
                      border: '1px solid var(--cbre-border)',
                      borderRadius: '4px'
                    }}
                  />
                ) : (
                  <div style={{ 
                    width: '100%', 
                    height: '200px', 
                    backgroundColor: 'var(--cbre-bg-secondary)',
                    border: '1px solid var(--cbre-border)',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--cbre-text-secondary)'
                  }}>
                    No image
                  </div>
                )}
                <div className="upload-controls" style={{ marginTop: '10px' }}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileChange('image2', e.target.files[0])}
                    className="form-input"
                    style={{ marginBottom: '5px' }}
                  />
                  <button
                    type="button"
                    onClick={() => handleImageUpload('image2')}
                    disabled={!imageUploads.image2.file || imageUploads.image2.uploading}
                    className="btn-primary"
                  >
                    {imageUploads.image2.uploading ? 'Uploading...' : 'Upload'}
                  </button>
                  {imageUploads.image2.status && (
                    <div className="upload-status" style={{ fontSize: '12px', marginTop: '5px' }}>
                      {imageUploads.image2.status}
                    </div>
                  )}
                </div>
              </div>

              {/* Image 3 */}
              <div style={{ flex: '1', minWidth: '250px' }}>
                <h4>Image 3</h4>
                {portfolioData.image3Link ? (
                  <img 
                    src={portfolioData.image3Link} 
                    alt="Image 3"
                    style={{ 
                      width: '100%', 
                      height: '200px', 
                      objectFit: 'cover',
                      border: '1px solid var(--cbre-border)',
                      borderRadius: '4px'
                    }}
                  />
                ) : (
                  <div style={{ 
                    width: '100%', 
                    height: '200px', 
                    backgroundColor: 'var(--cbre-bg-secondary)',
                    border: '1px solid var(--cbre-border)',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--cbre-text-secondary)'
                  }}>
                    No image
                  </div>
                )}
                <div className="upload-controls" style={{ marginTop: '10px' }}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileChange('image3', e.target.files[0])}
                    className="form-input"
                    style={{ marginBottom: '5px' }}
                  />
                  <button
                    type="button"
                    onClick={() => handleImageUpload('image3')}
                    disabled={!imageUploads.image3.file || imageUploads.image3.uploading}
                    className="btn-primary"
                  >
                    {imageUploads.image3.uploading ? 'Uploading...' : 'Upload'}
                  </button>
                  {imageUploads.image3.status && (
                    <div className="upload-status" style={{ fontSize: '12px', marginTop: '5px' }}>
                      {imageUploads.image3.status}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortfolioDetails;
